// ============================================================
// src/features/dashboard/Dashboard.tsx   (Plan-based navigation)
// ============================================================

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Alert,
  AlertTitle,
  Paper,
  Skeleton,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import WeeklyMealGrid from "./WeeklyMealGrid";
import TodaysDashboard from "./TodaysDashboard";
import MealPlanOptionsDialog from "./MealPlanOptionsDialog";
import api, { getAuthToken } from "../../services/api";
import { API_BASE } from "../../config";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ShoppingCart, ArrowBack } from "@mui/icons-material";
import { trackEvent, AnalyticsEvents } from "../../services/analytics";

// Enable UTC plugin for timezone-safe date handling
dayjs.extend(utc);

// Rotating status messages shown during meal plan generation. Light, factual,
// hints at what's actually happening inside the agent — not just a spinner.
// Replace these freely; tone is the only thing that matters here.
const WITTY_MESSAGES = [
  "Searching the recipe library...",
  "Avoiding ingredients your family doesn't like...",
  "Balancing protein across the week...",
  "Checking dietary restrictions...",
  "Finding kid-friendly options...",
  "Picking dinners under 45 minutes...",
  "Making sure no one gets the same meal twice...",
  "Crafting a balanced shopping list...",
  "Picking meals everyone will actually eat...",
  "Almost there — putting it all together...",
];

// Helper to format dates without timezone shift
// Parses as UTC to prevent local timezone from shifting the date
const formatDate = (dateValue: string | Date, format: string = 'MMM D') => {
  if (!dateValue) return '';
  return dayjs.utc(dateValue).format(format);
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get mode from URL (default to 'today', other options: 'plan', 'generate')
  const mode = searchParams.get('mode') || 'today';
  const planId = searchParams.get('planId');

  const [activePlan, setActivePlan] = useState<any | null>(null);
  const [nextPlan, setNextPlan] = useState<any | null>(null);
  const [futurePlans, setFuturePlans] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  // Generation state
  const [progressText, setProgressText] = useState<string | null>(null);
  const [jobRunning, setJobRunning] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // Rotating-message index — advances every 4s while a generation is active.
  // Index is stable when generation is idle so the message doesn't flicker
  // through the list when nothing's happening.
  const [rotMsgIdx, setRotMsgIdx] = useState(0);

  useEffect(() => {
    if (!progressText) return;
    const t = setInterval(() => {
      setRotMsgIdx((i) => (i + 1) % WITTY_MESSAGES.length);
    }, 6000);
    return () => clearInterval(t);
  }, [progressText]);

  /** ---------------------------------------------------------------- */
  /** Fetch user's meal plans (active, next, future)                  */
  /** ---------------------------------------------------------------- */
  const fetchUserPlans = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/meal-plans/user-plans');
      setActivePlan(data.activePlan || null);
      setNextPlan(data.nextPlan || null);
      setFuturePlans(data.futurePlans || []);

      console.log('Fetched plans:', data);
    } catch (err) {
      console.error("Error loading meal plans", err);
    }
    setLoading(false);
  }, []);

  /** ---------------------------------------------------------------- */
  /** Fetch specific plan with meals by ID                            */
  /** ---------------------------------------------------------------- */
  const fetchPlanById = useCallback(async (id: number) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/meal-plans/${id}`);
      setSelectedPlan(data);
    } catch (err) {
      console.error("Error loading meal plan", err);
      setSelectedPlan(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUserPlans();
  }, [fetchUserPlans]);

  // Fetch specific plan when planId changes
  useEffect(() => {
    if (planId) {
      fetchPlanById(parseInt(planId));
    } else {
      setSelectedPlan(null);
    }
  }, [planId, fetchPlanById]);

  // Auto-redirect to include planId when accessing plan/week mode without one
  useEffect(() => {
    if ((mode === 'plan' || mode === 'week') && !planId && !loading) {
      // Default to active plan, then next plan, then first future plan
      const defaultPlan = activePlan || nextPlan || futurePlans[0];
      if (defaultPlan) {
        navigate(`/dashboard?mode=plan&planId=${defaultPlan.plan_id}`, { replace: true });
      }
    }
  }, [mode, planId, loading, activePlan, nextPlan, futurePlans, navigate]);

  // Auto-fetch available slots when entering generate mode
  useEffect(() => {
    if (mode === 'generate' && !dialogOpen && !jobRunning && !progressText) {
      fetchAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  /** ---------------------------------------------------------------- */
  /** Fetch available slots for meal plan generation                  */
  /** ---------------------------------------------------------------- */
  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const { data } = await api.get('/meal-plans/available-slots');
      setAvailableSlots(data.slots || []);

      if (data.slots && data.slots.length === 1) {
        const slot = data.slots[0];
        if (slot.isPartial) {
          const endDateStr = slot.weekEnd || dayjs.utc(slot.endDate).format('YYYY-MM-DD');
          await handleGenerateWithDates(slot.weekStart, endDateStr);
        } else {
          await handleGenerateWithDates(slot.weekStart);
        }
      } else if (data.slots && data.slots.length > 1) {
        setLoadingSlots(false);
        setDialogOpen(true);
      } else {
        // No slots available
        setLoadingSlots(false);
      }
    } catch (err) {
      console.error('Error fetching available slots:', err);
      alert('Failed to fetch available meal plan options. Please try again.');
      setLoadingSlots(false);
    }
  };

  /** ---------------------------------------------------------------- */
  /** Generate meal plan with specific dates                           */
  /** ---------------------------------------------------------------- */
  const handleGenerateWithDates = async (weekStart?: string, weekEnd?: string) => {
    setDialogOpen(false);
    setJobRunning(true);
    setLoadingSlots(false);
    setProgressText("Preparing meal plan...");

    try {
      const requestData: any = {};
      if (weekStart) requestData.weekStart = weekStart;
      if (weekEnd) requestData.weekEnd = weekEnd;

      const { data } = await api.post("/meal-plans/request", requestData);

      if (data.error && data.planId) {
        const confirmed = window.confirm(`${data.error}. Would you like to view it?`);
        if (confirmed) {
          navigate(`/meal-plans/${data.planId}/review`);
        } else {
          navigate('/dashboard');
        }
        setJobRunning(false);
        setProgressText(null);
        return;
      }

      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      const sseUrl = `${API_BASE}/meal-plans/progress/${data.jobId}?token=${token}`;
      await new Promise(resolve => setTimeout(resolve, 100));

      const es = new EventSource(sseUrl);
      es.onopen = () => console.log("EventSource connected");
      es.onerror = (e) => console.error("EventSource error:", e);

      es.onmessage = (ev) => {
        const evt = JSON.parse(ev.data);

        if (evt.stage === "gather-data") {
          setProgressText("Gathering family preferences...");
        }
        if (evt.stage === "stream") {
          setProgressText("Talking to MealGen AI...");
        }
        if (evt.stage === "search-recipes") {
          setProgressText(evt.message || "Searching for recipes...");
        }
        if (evt.stage === "create-recipe") {
          setProgressText(evt.message || "Creating a new recipe...");
        }
        if (evt.stage === "select-recipe") {
          setProgressText(evt.message || "Building your meal plan...");
        }
        if (evt.stage === "persist-db") {
          setProgressText("Saving meals to database...");
        }
        if (evt.stage === "done") {
          es.close();
          setProgressText(null);
          setJobRunning(false);
          trackEvent(AnalyticsEvents.MEAL_PLAN_GENERATED, { plan_id: evt.planId });
          setTimeout(() => {
            navigate(`/meal-plans/${evt.planId}/review`);
          }, 500);
        }
        if (evt.stage === "error") {
          es.close();
          setProgressText(null);
          setJobRunning(false);
          alert("Meal plan generation failed: " + evt.message);
          navigate('/dashboard');
        }
      };
    } catch (err: any) {
      setProgressText(null);
      setJobRunning(false);
      alert(err.response?.data?.message || "Failed to start meal generation");
      navigate('/dashboard');
    }
  };

  /** ---------------------------------------------------------------- */
  /** Navigation handlers                                             */
  /** ---------------------------------------------------------------- */
  const handleViewPlan = (plan: any) => {
    navigate(`/dashboard?mode=plan&planId=${plan.plan_id}`);
  };

  /** ---------------------------------------------------------------- */
  /** Render                                                           */
  /** ---------------------------------------------------------------- */
  const displayPlan = selectedPlan || activePlan;
  const isPlanApproved = displayPlan && (displayPlan.status === 'approved' || displayPlan.status === 'active' || displayPlan.status === 'completed');
  const isPlanPlanned = displayPlan && displayPlan.status === 'planned';

  // If mode is 'today', show the new TodaysDashboard
  if (mode === 'today') {
    return <TodaysDashboard />;
  }

  // If mode is 'generate', show generation UI
  if (mode === 'generate') {
    return (
      <Box sx={{ mt: 4, px: 3, textAlign: 'center' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 4 }}
        >
          Back to Today
        </Button>

        <Typography variant="h4" gutterBottom>
          Generate New Meal Plan
        </Typography>

        {/* While we're just loading available start-day slots (pre-generation),
            keep the lightweight progress bar. The richer skeleton + rotating
            copy only shows during actual plan generation. */}
        {loadingSlots && !progressText && (
          <Box sx={{ mt: 4, maxWidth: 400, mx: 'auto' }}>
            <Typography variant="subtitle1" gutterBottom>
              Loading available options...
            </Typography>
            <LinearProgress sx={{ mt: 2 }} />
          </Box>
        )}

        {progressText && (
          <Box sx={{ mt: 4 }}>
            {/* Rotating witty primary message + actual backend stage as
                secondary caption. The primary line is what users feel; the
                caption confirms something concrete is happening (and lets us
                tell when generation has stalled). */}
            <Typography variant="h6" sx={{ minHeight: 32 }}>
              {WITTY_MESSAGES[rotMsgIdx]}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', minHeight: 18 }}
            >
              {progressText}
            </Typography>
            <LinearProgress sx={{ mt: 2, maxWidth: 400, mx: 'auto' }} />

            {/* Skeleton "weekly grid" — gives the wait a sense of structure
                and previews the shape of the result. A1 (streaming meal
                names) will later fill these in real time as the agent picks
                them; for now they pulse until generation completes. */}
            <Box
              sx={{
                mt: 4,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 2,
                maxWidth: 1000,
                mx: 'auto',
              }}
            >
              {Array.from({ length: 7 }).map((_, i) => (
                <Paper key={i} sx={{ overflow: 'hidden' }}>
                  <Skeleton variant="rectangular" height={140} animation="wave" />
                  <Box sx={{ p: 2 }}>
                    <Skeleton width="80%" animation="wave" />
                    <Skeleton width="60%" animation="wave" />
                    <Skeleton width="40%" animation="wave" />
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {!loadingSlots && !progressText && availableSlots.length === 0 && (
          <Box sx={{ mt: 4 }}>
            <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto' }}>
              <AlertTitle>No Available Slots</AlertTitle>
              You already have meal plans for all upcoming weeks. Complete or delete an existing plan to generate a new one.
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 3 }}
            >
              Back to Dashboard
            </Button>
          </Box>
        )}

        <MealPlanOptionsDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            navigate('/dashboard');
          }}
          onSelect={handleGenerateWithDates}
          slots={availableSlots}
          loading={loadingSlots}
        />
      </Box>
    );
  }

  // Otherwise show the plan view
  return (
    <>
    {mode === 'plan' && (
      <Box sx={{ mb: 2, px: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
        >
          Back to Today
        </Button>
      </Box>
    )}
    <Box sx={{ mb: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
      {activePlan && (
        <Button
          variant={!selectedPlan || selectedPlan.plan_id === activePlan.plan_id ? "contained" : "outlined"}
          onClick={() => handleViewPlan(activePlan)}
        >
          Active Plan ({formatDate(activePlan.actual_start_date)} - {formatDate(activePlan.actual_end_date)})
        </Button>
      )}
      {nextPlan && (
        <Button
          variant={selectedPlan && selectedPlan.plan_id === nextPlan.plan_id ? "contained" : "outlined"}
          onClick={() => handleViewPlan(nextPlan)}
        >
          Next Plan ({formatDate(nextPlan.actual_start_date)} - {formatDate(nextPlan.actual_end_date)})
        </Button>
      )}
      {futurePlans.map((plan) => (
        <Button
          key={plan.plan_id}
          variant={selectedPlan && selectedPlan.plan_id === plan.plan_id ? "contained" : "outlined"}
          onClick={() => handleViewPlan(plan)}
        >
          Future Plan ({formatDate(plan.actual_start_date)})
        </Button>
      ))}
      {displayPlan && isPlanApproved && (
        <Button
          variant="outlined"
          startIcon={<ShoppingCart />}
          onClick={() => navigate(`/shopping-list/${displayPlan.plan_id}`)}
        >
          Shopping List
        </Button>
      )}
    </Box>
    <Box mt={4} px={3}>
      {/* Alert banner for unapproved plans */}
      {isPlanPlanned && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Meal Plan Needs Approval</AlertTitle>
          You have a meal plan that needs to be reviewed and approved before you can view recipe details or generate a shopping list.
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => navigate(`/meal-plans/${displayPlan.plan_id}/review`)}
            >
              Review & Approve Now
            </Button>
          </Box>
        </Alert>
      )}

      {/* Alert banner for dietary violations */}
      {displayPlan?.dietary_violations?.hasViolations && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Review Dietary Restrictions</AlertTitle>
          {displayPlan.dietary_violations.message || 'Some recipes may contain ingredients that don\'t match your dietary restrictions. Please review ingredients before cooking.'}
        </Alert>
      )}

      {loading ? (
        <Box mt={6} textAlign="center">
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {displayPlan ? (
            <>
              <Typography variant="h4" gutterBottom>
                {formatDate(displayPlan.actual_start_date || displayPlan.week_start_date, "MMMM D")} - {formatDate(displayPlan.actual_end_date || dayjs.utc(displayPlan.week_start_date).add(6, 'day').format('YYYY-MM-DD'), "MMMM D, YYYY")}
              </Typography>
              <WeeklyMealGrid
                mealsByDay={(() => {
                  // Use actual_start_date if available, otherwise fall back to week_start_date
                  // Parse as UTC to avoid timezone shifting dates
                  const startDate = displayPlan.actual_start_date || displayPlan.week_start_date;
                  const endDate = displayPlan.actual_end_date || dayjs.utc(displayPlan.week_start_date).add(6, 'day').format('YYYY-MM-DD');

                  // Calculate number of days (for partial weeks or full weeks)
                  const numDays = dayjs.utc(endDate).diff(dayjs.utc(startDate), 'day') + 1;

                  return Array.from({ length: numDays }, (_, i) => {
                    const date = dayjs.utc(startDate).add(i, "day");
                    const dayLabel = date.format("dddd");
                    const order = { breakfast: 1, lunch: 2, dinner: 3 } as const;

                    return {
                      day: dayLabel,
                      meals:
                        Array.isArray(displayPlan.meals)
                          ? displayPlan.meals
                              .filter((meal: any) => dayjs.utc(meal.assigned_date).isSame(date, "day"))
                              .sort((a: any, b: any) => {
                                const orderA = order[a.meal_type as keyof typeof order] || 99;
                                const orderB = order[b.meal_type as keyof typeof order] || 99;
                                return orderA - orderB;
                              })
                              .map((meal: any) => ({
                                meal_id: meal.meal_id,
                                meal_type: meal.meal_type,
                                recipe: meal.recipe,
                                status: meal.status,
                                completed_at: meal.completed_at,
                                is_leftover: meal.is_leftover,
                                extra_servings: meal.extra_servings,
                                servings: meal.servings,
                              }))
                          : [],
                    };
                  });
                })()}
                planStatus={displayPlan.status}
                planId={displayPlan.plan_id}
              />
            </>
          ) : (
            <Box textAlign="center" sx={{ mt: 4 }}>
              <Typography color="text.secondary" gutterBottom>
                No meal plan found.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
                sx={{ mt: 2 }}
              >
                Go to Today's Dashboard
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
    </>
  );
};

export default Dashboard;
